import { useState } from "react";
import { useTranslation } from "react-i18next";
import { GraduationCap } from "lucide-react";
import { SimulationBoard } from "./SimulationBoard";
import { lt } from "../localize";
import type { LessonStage } from "../types";

interface StageHookProps {
  stage: LessonStage;
  onAnswer: (index: number) => void;
}

export const StageHook = ({ stage, onAnswer }: StageHookProps) => {
  const { i18n } = useTranslation();
  const lang = i18n.language;
  const [selected, setSelected] = useState<number | null>(null);
  const hook = stage.hook;

  if (!hook) return null;

  const handleSelect = (i: number) => {
    if (selected !== null) return;
    setSelected(i);
    onAnswer(i);
  };

  return (
    <div className="space-y-5">
      {/* Scenario */}
      <SimulationBoard simulation={hook.scenario} />

      {/* Coach avatar + speech bubble question */}
      <div className="relative">
        {/* Decorative "?" background */}
        <div
          className="absolute -top-2 -right-1 text-[120px] font-black leading-none pointer-events-none select-none opacity-[0.08]"
          aria-hidden="true"
        >
          ?
        </div>

        {/* Coach avatar */}
        <div
          className="w-8 h-8 rounded-full flex items-center justify-center mb-2"
          style={{ backgroundColor: "hsl(var(--level-accent) / 0.2)" }}
        >
          <GraduationCap
            className="w-4 h-4"
            style={{ color: "hsl(var(--level-accent))" }}
          />
        </div>

        {/* Speech bubble */}
        <div className="relative glass p-4">
          {/* Triangle pointer at top-left */}
          <div
            className="absolute -top-2 left-4 w-4 h-4 rotate-45 border-l border-t border-white/[0.08]"
            style={{ backgroundColor: "rgba(255,255,255,0.04)" }}
          />
          <p className="relative font-bold text-sm leading-relaxed">
            {lt(hook.question, lang)}
          </p>
        </div>
      </div>

      {/* Options — large card-shaped buttons */}
      <div className="space-y-2.5">
        {hook.options.map((opt, i) => (
          <button
            key={i}
            onClick={() => handleSelect(i)}
            className={`w-full glass py-4 px-4 text-left text-sm rounded-2xl transition-all duration-300 ${
              selected === i
                ? "!border-secondary !bg-secondary/10 border-l-4 !border-l-secondary"
                : selected === null
                  ? "hover:bg-white/[0.07] border-l-4"
                  : "opacity-40 border-l-4"
            }`}
            style={
              selected === null || selected === i
                ? { borderLeftColor: selected === i ? undefined : "hsl(var(--level-accent) / 0.5)" }
                : { borderLeftColor: "transparent" }
            }
            disabled={selected !== null}
          >
            {lt(opt.text, lang)}
          </button>
        ))}
      </div>

      {/* "Discover the answer" hint */}
      {selected !== null && (
        <p
          className="text-xs text-muted-foreground text-center italic animate-fade-in-up"
        >
          {lt({ es: "Descubriras la respuesta durante la leccion...", en: "You'll discover the answer during the lesson..." }, lang)}
        </p>
      )}
    </div>
  );
};
